import React from 'react';
import { Box, Button, Group, MultiSelect, Paper, Select, SimpleGrid, Text, TextInput, Textarea, Title } from '@mantine/core';
import { IconSearch } from '@tabler/icons-react';
import { entityService } from '../../services/entityServiceSingleton';
import { GenericTable, type TableColumn } from '../common/GenericTable';
import { CommonPagination } from '../common/Pagination';
import { useEntityTypes } from '../../contexts/EntityTypesContext';
import { getPluralizedLabel } from '../../utils/pluralizationUtils';
import { GuidDisplay } from '../common/GuidDisplay';
import { useFieldOptionsForEntityType, useLoadSchemaData } from '../../store/schemaHooks';
import { getSearchInAttributesForSchema } from '../../utils/searchInOptions';
import { processSearchQueryForSearchtext } from '../../utils/scimSearchUtils';

type Dialect = 'freetext' | 'expression' | 'custom';

type Row = Record<string, any>;

function getId(row: Row): string {
    return String(row?.Id ?? row?.id ?? '');
}

function getDisplayName(row: Row): string {
    return String(row?.DisplayName ?? row?.displayName ?? row?.Name ?? row?.name ?? '');
}

function buildDefaultColumns(): TableColumn<Row>[] {
    return [
        {
            key: 'DisplayName',
            label: 'Display Name',
            title: 'DisplayName',
            sortable: false,
            render: (row: Row) => getDisplayName(row) || '—'
        },
        {
            key: 'Id',
            label: 'Id',
            title: 'Id',
            sortable: false,
            render: (row: Row) => {
                const id = getId(row);
                if (!id) return '—';
                return <GuidDisplay value={id} variant="compact" showCopyButton={true} showExpandButton={true} />;
            }
        }
    ];
}

function normalizeEntityTypeForApi(entityType: string): string {
    return (entityType || '').trim().toLowerCase().replace(/\s+/g, '');
}

function isHighPrioritySearchAttribute(attribute: string): boolean {
    const key = (attribute || '').toLowerCase();
    return key === 'id' || key === 'displayname' || key === 'userprincipalname';
}

function dialectLabel(dialect: Dialect): string {
    if (dialect === 'freetext') return 'Free text';
    if (dialect === 'expression') return 'Expression';
    return 'Custom filter';
}

function dialectDescription(dialect: Dialect): string {
    if (dialect === 'freetext') return 'searchdialect=freetext (searchText)';
    if (dialect === 'expression') return 'searchdialect=expression (searchText)';
    return 'filter=<SCIM filter> (no searchText)';
}

function SearchPanel(props: {
    entityType: string;
    dialect: Dialect;
}) {
    const { entityType, dialect } = props;
    const fieldOptions = useFieldOptionsForEntityType(entityType);

    const searchInOptions = React.useMemo(() => {
        const available = (fieldOptions || []).map((o: any) => o?.name).filter(Boolean);
        const result = getSearchInAttributesForSchema(entityType, available);
        return result.all;
    }, [entityType, fieldOptions]);

    const searchInSelectData = React.useMemo(() => {
        const highPriority = searchInOptions.filter(isHighPrioritySearchAttribute);
        const others = searchInOptions.filter((attr) => !isHighPrioritySearchAttribute(attr));
        if (highPriority.length > 0 && others.length > 0) {
            return [
                { group: 'High Priority', items: highPriority.map((attr) => ({ value: attr, label: attr })) },
                { group: 'Other Search Attributes', items: others.map((attr) => ({ value: attr, label: attr })) },
            ];
        }
        return searchInOptions.map((attr) => ({ value: attr, label: attr }));
    }, [searchInOptions]);

    const [draft, setDraft] = React.useState('');
    const [applied, setApplied] = React.useState('');
    const [page, setPage] = React.useState(1);
    const [loading, setLoading] = React.useState(false);
    const [error, setError] = React.useState<string | null>(null);
    const [data, setData] = React.useState<Row[]>([]);
    const [totalResults, setTotalResults] = React.useState(0);
    const [lastRelativeUrl, setLastRelativeUrl] = React.useState<string>('');
    const [searchIn, setSearchIn] = React.useState<string[]>(['DisplayName']);

    React.useEffect(() => {
        // Reset when entity changes.
        setDraft('');
        setApplied('');
        setPage(1);
        setError(null);
        setData([]);
        setTotalResults(0);
        setSearchIn(['DisplayName']);
    }, [entityType, dialect]);

    const doSearch = React.useCallback(async (nextApplied: string, nextPage: number) => {
        const normalized = normalizeEntityTypeForApi(entityType);
        const trimmed = (nextApplied || '').trim();
        if (!normalized) return;

        setLoading(true);
        setError(null);
        try {
            const pageSize = 25;
            const attrs = ['Id', 'DisplayName'];

            const isCustom = dialect === 'custom';
            const searchtext = !isCustom && trimmed ? (processSearchQueryForSearchtext(trimmed, searchIn) ?? trimmed) : undefined;
            const filter = isCustom && trimmed ? trimmed : undefined;
            const omitSearchDialectExpression = dialect === 'freetext';
            const searchAttributesParam = !isCustom && searchIn && searchIn.length > 0 ? searchIn.join(',') : undefined;

            const startIndex = ((nextPage - 1) * pageSize) + 1;
            const qp: Record<string, string> = {
                attributes: attrs.join(','),
                skipReferenceExpansion: 'true',
                startIndex: String(startIndex),
                count: String(pageSize),
                sortBy: 'DisplayName:ascending',
            };
            if (filter) qp.filter = filter;
            if (searchtext) {
                qp.searchText = searchtext;
                qp.searchdialect = omitSearchDialectExpression ? 'freetext' : 'expression';
            }
            if (searchAttributesParam) {
                qp.searchAttributes = searchAttributesParam;
            }
            // Keep commas readable in the displayed URL.
            const qs = new URLSearchParams(qp).toString().replace(/%2C/gi, ',');
            setLastRelativeUrl(`/entities/${normalized}?${qs}`);

            const result = await entityService.getEntitiesByType(
                normalized,
                nextPage,
                pageSize,
                'DisplayName:ascending',
                filter,
                attrs,
                undefined,
                searchtext,
                searchAttributesParam,
                undefined,
                undefined,
                omitSearchDialectExpression
            );

            const rows = (result?.items ?? result?.data ?? []) as Row[];
            setData(Array.isArray(rows) ? rows : []);
            setTotalResults(Number(result?.totalResults ?? 0) || 0);
        } catch (e: any) {
            setError(e?.message ? String(e.message) : 'Search failed');
            setData([]);
            setTotalResults(0);
            setLastRelativeUrl('');
        } finally {
            setLoading(false);
        }
    }, [entityService, entityType, dialect, searchIn]);

    const handleApply = React.useCallback(() => {
        const next = draft.trim();
        setApplied(next);
        setPage(1);
        void doSearch(next, 1);
    }, [draft, doSearch]);

    const totalPages = Math.max(1, Math.ceil(totalResults / 25));

    return (
        <Paper withBorder p="md">
            <Group justify="space-between" align="flex-start" wrap="wrap" mb="xs">
                <Box>
                    <Title order={5}>{dialectLabel(dialect)}</Title>
                    <Text size="xs" c="dimmed">{dialectDescription(dialect)}</Text>
                </Box>
            </Group>

            {(dialect === 'freetext' || dialect === 'expression') && (
                <MultiSelect
                    label="Search In"
                    size="sm"
                    data={searchInSelectData as any}
                    value={searchIn}
                    onChange={setSearchIn}
                    clearable={false}
                    mb="xs"
                />
            )}

            {dialect === 'custom' ? (
                <Textarea
                    value={draft}
                    onChange={(e) => setDraft(e.currentTarget.value)}
                    autosize
                    minRows={2}
                    maxRows={8}
                    placeholder='e.g. userprincipalname co "HoganR"'
                    mb="xs"
                />
            ) : (
                <TextInput
                    value={draft}
                    onChange={(e) => setDraft(e.currentTarget.value)}
                    placeholder='Search…'
                    leftSection={<IconSearch size={14} />}
                    mb="xs"
                    onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                            e.preventDefault();
                            handleApply();
                        }
                    }}
                />
            )}

            <Group justify="flex-end" mb="sm">
                <Button size="xs" onClick={handleApply} disabled={!draft.trim()}>
                    Search
                </Button>
            </Group>

            {error && (
                <Text size="xs" c="red" mb="xs">
                    {error}
                </Text>
            )}

            <GenericTable<Row>
                data={data}
                columns={buildDefaultColumns()}
                isLoading={loading}
                getRowKey={(row) => getId(row)}
                emptyMessage={applied ? 'No results' : 'Enter a query and search'}
                entityType={entityType}
                isFullscreen={false}
            />

            {totalResults > 0 && (
                <Box mt="sm">
                    <CommonPagination
                        currentPage={page}
                        totalPages={totalPages}
                        totalResults={totalResults}
                        onPageChange={(p) => {
                            setPage(p);
                            void doSearch(applied, p);
                        }}
                        itemsPerPage={25}
                    />
                </Box>
            )}

            {lastRelativeUrl && (
                <Text mt="sm" size="xs" ff="monospace" style={{ wordBreak: 'break-all' }} c="dimmed">
                    {lastRelativeUrl}
                </Text>
            )}
        </Paper>
    );
}

export default function SearchCompare() {
    // Ensure schema data is loaded so Search In selectors have full attribute lists.
    useLoadSchemaData(true);
    const { entityTypes } = useEntityTypes();
    const options = React.useMemo(() => {
        const list = (entityTypes ?? [])
            .map((t: any) => String(t?.name ?? '').trim())
            .filter(Boolean);
        const unique = Array.from(new Set(list));
        unique.sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
        return unique.map((name) => ({ value: name, label: getPluralizedLabel(name) }));
    }, [entityTypes]);

    const [entityType, setEntityType] = React.useState<string>('Person');

    return (
        <Box p="md">
            <Group justify="space-between" align="flex-end" wrap="wrap" mb="md">
                <Box>
                    <Title order={4}>Search Compare</Title>
                    <Text size="sm" c="dimmed">
                        Compare free text, expression, and custom filter results side-by-side. Ignores user settings.
                    </Text>
                </Box>
                <Select
                    label="Entity type"
                    value={entityType}
                    onChange={(v) => setEntityType(v ?? 'Person')}
                    data={options}
                    searchable
                    w={320}
                />
            </Group>

            <SimpleGrid cols={{ base: 1, md: 3 }} spacing="md" verticalSpacing="md">
                <SearchPanel entityType={entityType} dialect="freetext" />
                <SearchPanel entityType={entityType} dialect="expression" />
                <SearchPanel entityType={entityType} dialect="custom" />
            </SimpleGrid>
        </Box>
    );
}

